const stripMarkdownJsonFence = (value) => {
  const text = String(value || "").trim();
  const fenceMatch = text.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  return fenceMatch ? fenceMatch[1].trim() : text;
};

const removeControlCharacters = (value) =>
  Array.from(String(value || ""))
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 31 && (code < 127 || code > 159);
    })
    .join("");

const extractFirstJsonObject = (text) => {
  const source = String(text || "");
  let start = -1;
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (char === "\\") {
      escaped = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (inString) continue;

    if (char === "{") {
      if (depth === 0) start = index;
      depth += 1;
      continue;
    }

    if (char === "}" && depth > 0) {
      depth -= 1;
      if (depth === 0 && start !== -1) {
        return source.slice(start, index + 1);
      }
    }
  }

  return null;
};

const parseGeneratedPostJson = (value) => {
  if (typeof value !== "string") return null;

  const cleanText = stripMarkdownJsonFence(value);
  const parseText = removeControlCharacters(cleanText);
  const candidates = [parseText];
  const extractedJson = extractFirstJsonObject(parseText);

  if (extractedJson && extractedJson !== parseText) {
    candidates.push(extractedJson);
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      // Thử candidate tiếp theo.
    }
  }

  return null;
};

const postTextFromValue = (value) => {
  if (typeof value === "string") return stripMarkdownJsonFence(value).trim();
  if (value == null) return "";

  if (typeof value === "object") {
    if (value.content != null) return postTextFromValue(value.content);
    if (value.text != null) return postTextFromValue(value.text);
  }

  return String(value).trim();
};

const imagePromptFromValue = (value) => {
  const prompt = postTextFromValue(value);
  return prompt.length > 700 ? prompt.slice(0, 700).trim() : prompt;
};

const getMarkdownSectionName = (line) => {
  const normalized = String(line || "")
    .trim()
    .replace(/^#{1,6}\s*/, "")
    .replace(/\*\*/g, "")
    .replace(/[:：]\s*$/, "")
    .trim()
    .toLowerCase();

  if (/^(title|tiêu đề|tieu de)$/.test(normalized)) return "title";
  if (/^(content|nội dung|noi dung)$/.test(normalized)) return "content";
  if (/^(validation|kiểm tra|kiem tra|đánh giá|danh gia)$/.test(normalized)) {
    return "validation";
  }

  if (/^facebook content marketing post$/.test(normalized)) return "skip";

  return null;
};

const cleanGeneratedPostContent = (content) =>
  String(content || "")
    .replace(/^\s*#{1,6}\s*(facebook content marketing post|title|content|validation|tiêu đề|nội dung|kiểm tra|đánh giá)\s*:?\s*$/gim, "")
    .replace(/^\s*\*\*(title|content|validation|tiêu đề|nội dung|kiểm tra|đánh giá)\s*:?\*\*\s*$/gim, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const parseMarkdownGeneratedPost = (
  value,
  fallbackTitle = "Nội dung được tạo"
) => {
  if (typeof value !== "string") return null;

  const lines = stripMarkdownJsonFence(value).split(/\r?\n/);
  const sections = {
    title: [],
    content: [],
  };
  let currentSection = null;
  let hasRecognizedSection = false;

  for (const line of lines) {
    const sectionName = getMarkdownSectionName(line);

    if (sectionName === "skip") {
      hasRecognizedSection = true;
      continue;
    }
    if (sectionName === "validation") {
      hasRecognizedSection = true;
      break;
    }
    if (sectionName === "title" || sectionName === "content") {
      hasRecognizedSection = true;
      currentSection = sectionName;
      continue;
    }

    if (currentSection === "title") {
      sections.title.push(line);
    } else if (currentSection === "content") {
      sections.content.push(line);
    } else if (line.trim() && !line.trim().startsWith("#")) {
      sections.content.push(line);
    }
  }

  if (!hasRecognizedSection) return null;

  const title = cleanGeneratedPostContent(sections.title.join("\n"))
    .split(/\r?\n/)
    .find((line) => line.trim());
  const content = cleanGeneratedPostContent(sections.content.join("\n"));

  if (!content) return null;

  return {
    title: title?.trim() || fallbackTitle,
    content,
  };
};

const isGenericFallbackTitle = (title) =>
  ["Bài viết từ URL context", "Nội dung được tạo"].includes(title);

export const normalizeGeneratedPostPayload = (payload = {}) => {
  const source =
    typeof payload === "string" ? parseGeneratedPostJson(payload) : payload;

  if (!source || typeof source !== "object" || Array.isArray(source)) {
    const parsedMarkdown = parseMarkdownGeneratedPost(payload);
    const content = parsedMarkdown
      ? parsedMarkdown.content
      : cleanGeneratedPostContent(postTextFromValue(payload));
    return { title: content, content };
  }

  const title = postTextFromValue(source.title);
  const content = postTextFromValue(source.content ?? source.text);
  const imagePrompt = imagePromptFromValue(
    source.imagePrompt ??
      source.imagePromptEnglish ??
      source.preparedImagePromptEnglish
  );
  const nestedPayload = parseGeneratedPostJson(content);

  if (nestedPayload) {
    const normalizedNested = normalizeGeneratedPostPayload(nestedPayload);
    const preferredTitle =
      title && !isGenericFallbackTitle(title)
        ? title
        : normalizedNested.title || title;

    return {
      title: preferredTitle,
      content: normalizedNested.content,
      ...((imagePrompt || normalizedNested.imagePrompt) && {
        imagePrompt: imagePrompt || normalizedNested.imagePrompt,
      }),
    };
  }

  const markdownNested = parseMarkdownGeneratedPost(content, title);
  if (markdownNested) {
    return {
      title:
        title && !isGenericFallbackTitle(title)
          ? title
          : markdownNested.title || title,
      content: markdownNested.content,
      ...(imagePrompt && { imagePrompt }),
    };
  }

  return {
    title: title || content,
    content: cleanGeneratedPostContent(content),
    ...(imagePrompt && { imagePrompt }),
  };
};
